import React, { useState, useEffect, useRef } from 'react';
import { View, Text, ScrollView, ActivityIndicator } from 'react-native';
import { styles, colors } from '../styles';
import { getHistorico } from '../services';
import { PhoneIcon } from '../icons';

const CallHistory = ({ userId, numero }) => {
  const [chamadas, setChamadas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Refs para controlar requisições
  const abortControllerRef = useRef(null);
  const isRequestingRef = useRef(false);
  const lastRequestKeyRef = useRef(null);

  useEffect(() => {
    // Verificar se temos os dados necessários primeiro
    if (!userId || !numero) {
      setChamadas([]);
      return;
    }

    // Criar chave única para esta combinação de parâmetros
    const requestKey = `${userId}-${numero || ''}`;
    
    // TRAVA: Se já está fazendo uma requisição, não iniciar nova
    if (isRequestingRef.current) {
      console.log('[TRAVA CALLHISTORY] Requisição já em andamento, ignorando nova requisição. Chave:', requestKey);
      return;
    }

    // Se já existe uma requisição ativa com os mesmos parâmetros, não iniciar nova
    if (lastRequestKeyRef.current === requestKey) {
      console.log('[TRAVA CALLHISTORY] Requisição duplicada ignorada (mesmos parâmetros). Chave:', requestKey);
      return;
    }

    // Se os parâmetros mudaram, cancelar todas as requisições anteriores
    if (abortControllerRef.current && lastRequestKeyRef.current !== requestKey) {
      console.log('[TRAVA CALLHISTORY] Cancelando requisições anteriores (parâmetros mudaram)');
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    // Marcar que estamos fazendo uma requisição
    isRequestingRef.current = true;
    lastRequestKeyRef.current = requestKey;
    
    // Resetar estados
    setChamadas([]);
    setError(null);
    setLoading(true);
    
    // Criar novo AbortController
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    getHistorico(userId, numero, abortController.signal)
      .then(({ chamadas }) => {
        // Verificar se a requisição foi cancelada
        if (abortController.signal.aborted) {
          return;
        }
        
        // Verificar se ainda é a requisição atual (parâmetros não mudaram)
        if (lastRequestKeyRef.current !== requestKey) {
          console.log('[TRAVA CALLHISTORY] Requisição obsoleta (parâmetros mudaram)');
          return;
        }
        
        const chamadasArray = chamadas || [];
        
        // Mapear campos do retorno: chamada_id, data, cliente_nome, destino, duracao
        const chamadasMapeadas = chamadasArray.map(chamada => ({
          chamada_id: chamada.chamada_id || '',
          data: chamada.data || '',
          cliente_nome: chamada.cliente_nome || '',
          destino: chamada.destino || '-',
          duracao: chamada.duracao || '00:00:00'
        }));
        
        setChamadas(chamadasMapeadas);
        setLoading(false);
      })
      .catch((err) => {
        // Ignorar erros de cancelamento
        if (err.message === 'AbortError' || abortController.signal.aborted) {
          return;
        }
        
        setError('Erro ao carregar histórico');
        setLoading(false);
      })
      .finally(() => {
        // Só resetar a flag se ainda estamos na mesma requisição
        const currentRequestKey = `${userId}-${numero || ''}`;
        if (abortControllerRef.current === abortController && lastRequestKeyRef.current === currentRequestKey) {
          isRequestingRef.current = false;
        }
      });
    
    // Cleanup: cancelar requisições quando numero mudar ou componente desmontar
    return () => {
      if (abortControllerRef.current === abortController) {
        console.log('[TRAVA CALLHISTORY] Cleanup: cancelando requisições');
        abortController.abort();
        abortControllerRef.current = null;
        isRequestingRef.current = false;
        lastRequestKeyRef.current = null;
      }
    };
  }, [userId, numero]);

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    // O formato já vem como "23/01/2026 20:49:07" do backend
    return dateTimeString;
  };

  if (!userId || !numero) {
    return (
      <View style={{ padding: 16 }}>
        <Text style={styles.textSmall}>Nenhum contato selecionado</Text>
      </View>
    );
  }

  return (
    <View style={{ flex: 1, padding: 12, minHeight: 0 }}>
      <View style={{ 
        borderTopWidth: 1,
        borderTopColor: colors.borderLight,
        paddingTop: 12,
        marginBottom: 12
      }}>
        <Text style={[styles.text, { fontWeight: '600', fontSize: 16 }]}>
          Histórico de Chamadas
        </Text>
      </View>

      {loading ? (
        <View style={[styles.loadingContainer, { minHeight: 150, paddingVertical: 40 }]}>
          <ActivityIndicator size="small" color={colors.textPrimary} />
          <Text style={[styles.textSmall, { marginTop: 12, opacity: 0.6 }]}>
            Carregando histórico...
          </Text>
        </View>
      ) : error ? (
        <View style={{ 
          padding: 20, 
          backgroundColor: '#FEE2E2', 
          borderRadius: 8, 
          alignItems: 'center' 
        }}>
          <Text style={[styles.errorText, { textAlign: 'center' }]}>{error}</Text>
        </View>
      ) : chamadas.length === 0 ? (
        <View style={{ 
          padding: 40, 
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <PhoneIcon size={48} color={colors.borderLight} />
          <Text style={[styles.textSmall, { marginTop: 16, opacity: 0.6, textAlign: 'center' }]}>
            Nenhuma chamada encontrada
          </Text>
        </View>
      ) : (
        <ScrollView 
          style={[styles.scrollView, { flex: 1 }]} 
          showsVerticalScrollIndicator={true}
          contentContainerStyle={{ paddingBottom: 8 }}
          nestedScrollEnabled={true}
          keyboardShouldPersistTaps="handled"
        >
          {chamadas.map((chamada, index) => (
            <View
              key={chamada.chamada_id || index}
              style={{
                paddingTop: 12,
                paddingBottom: 12,
                paddingHorizontal: 12,
                marginBottom: 8,
                borderBottomWidth: 1,
                borderBottomColor: '#E5E7EB',
                backgroundColor: colors.bgPrimary,
                borderRadius: 8,
              }}
            >
              {/* Data e Hora */}
              <Text style={[styles.text, { 
                fontSize: 14, 
                fontWeight: '600',
                marginBottom: 4
              }]}>
                {formatDateTime(chamada.data)}
              </Text>
              
              {/* Cliente */}
              <Text style={[styles.textSmall, { 
                opacity: 0.8,
                marginBottom: 4
              }]}>
                {chamada.cliente_nome || 'Cliente não informado'}
              </Text>
              
              {/* Destino e Duração */}
              <View style={{ 
                flexDirection: 'row', 
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 4
              }}>
                <Text style={[styles.textSmall, { opacity: 0.7 }]}>
                  Destino: {chamada.destino}
                </Text>
                <Text style={[styles.textSmall, { 
                  opacity: 0.7,
                  fontWeight: '500'
                }]}>
                  Duração: {chamada.duracao}
                </Text>
              </View>
            </View>
          ))}
        </ScrollView>
      )}
    </View>
  );
};

export default React.memo(CallHistory);
